// Thin LOCAL fetch client for the patient prescriptions endpoint (read-only, own-records-only).
// Deliberately NOT in the published @forcebase-ai SDK (local-first — mirrors conversationsApi /
// the doctor-side clinicalFactsApi). Carries the SDK HttpClient shape: baseUrl + getToken → Bearer,
// credentials:"include" (dev cookie), unwraps the { data } envelope. The free-text fields arrive
// already decrypted from the API (server-side only); we never decrypt on the client.
//
// FB-authorized (Slack, Jun 19 — Thomas): patient-visible prescriptions. This file names prescribing
// terms in its types and is therefore allowlisted in scripts/check-clinical-terms.mjs.
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

export interface PatientPrescription {
  id: string;
  drug: string;
  dosage: string;
  sig: string;
  notes: string | null;
  status: "active" | "discontinued";
  created_at: string;
}

export interface PrescriptionsApi {
  list(): Promise<PatientPrescription[]>;
}

export function usePrescriptionsApi(): PrescriptionsApi {
  const { getToken } = useAppAuth();
  return useMemo<PrescriptionsApi>(() => {
    async function request<T>(path: string): Promise<T> {
      const headers: Record<string, string> = {};
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}${path}`, {
        method: "GET",
        headers,
        credentials: "include",
      });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const err = (json as { error?: { message?: string } }).error;
        throw new Error(err?.message ?? "Request failed");
      }
      return (json as { data?: T }).data as T;
    }
    return {
      list: () =>
        request<PatientPrescription[]>("/v1/patient/prescriptions").then((d) => d ?? []),
    };
  }, [getToken]);
}
